import {Component, Input, Output, OnDestroy, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Store} from '@ngrx/store';
import {ShipmentService} from '@services/shipment.service';
import {FontAwesomeService} from '@services/font-awesome.service';
import {AppState, SearchResult, selectFooterAction, selectSearchResults, } from '@store/reducers';
import {ACTIVE_SEARCH_REMOVE, DeleteAllShipmentsInCart, SEARCH_RESULT_UPDATE} from '@store/actions';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import {CreateConsignmentService} from '@modules/consignment/create-consignment.service';
import {Router} from '@angular/router';
import {FooterActionEnum} from '@models/footer-action.enum';
import {ClearOrderCreationState} from '@store/actions/order-creation.action';
import 'rxjs/add/operator/map';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss']
})

export class SearchResultsComponent implements OnInit, OnDestroy {
  public searchResults: SearchResult[] = [];
  public showOverlay = false;
  public spinnerLoadingPercent = 0;
  private unsubscribe: Subject<void> = new Subject();
  private deliveryTypes: any[];

  @Input() parent: FormGroup;
  @Input() submitted: boolean;

  constructor(
    private shipmentService: ShipmentService,
    private faService: FontAwesomeService,
    private store: Store<AppState>,
    private consignmentService: CreateConsignmentService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.store.dispatch(new DeleteAllShipmentsInCart());
    this.store.select(selectSearchResults)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.searchResults = data.map(searchResult => {
          let products = [...searchResult.products];

          if (!products.length) {
            return searchResult;
          }

          products = products.sort((a, b) => this.sortByPrice('price', a, b));
          const cheapestProduct = products[0];
          products = products.splice(1).sort((a, b) => this.sortFn('time_until_delivery', a, b));
          // TODO: Fix error when only 1 product is in the result.
          // const fastestProduct = products[0];
          // products = products.splice(1).sort((a, b) => a.name.localeCompare(b.name) || a.product_name.localeCompare(b.product_name));

          return {...searchResult, products: [cheapestProduct/*, fastestProduct*/, ...products]};
        }).sort((b, a) => this.sortFn('search_id', a, b));
        this.hideOverLayWhenAllAreLoaded();
      });

    // console.log(this.searchResults);

    this.searchResults.forEach((searchResult: any) => {
      // console.log(searchResult);
      // console.log(searchResult);
      // this.searchResults = this.searchResults.sort((b, a) => this.sortFn('search_id', a, b));
      // console.log('init - search_id: ' + searchResult.search_id);
      // console.log('init - loaded: ' + searchResult.loaded);
      // console.log('init - searchResults.length: ' + this.searchResults.length);
      // this.handleSearch(searchResult, false);
      if (!searchResult.loaded) {
        this.handleSearch(searchResult);
      }
    });

    this.shipmentService.getInitialData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.deliveryTypes = this.shipmentService.getData('delivery_types'));

    this.store.select(selectFooterAction)
      .subscribe(action => {
        switch (action) {
          case FooterActionEnum.GO_TO_CREATE_CONSIGNMENT:
            this.router.navigate(['create-consignment']);
            this.store.dispatch(new ClearOrderCreationState());
            break;
          case FooterActionEnum.GO_TO_CREATE_CONSIGNMENT_ORDER_SUMMARY:
            this.router.navigate(['create-consignment', 'order-summary']);
            break;
        }
      });
  }

  sortFn = (parameter, a, b) => a[parameter] - b[parameter];

  sortByPrice = (parameter, a, b) => {
    const aTotalAmountAmount = a.price + this.calculateRequiredAdditionalServicesAmount(a).serviceAmount;
    const bTotalAmountAmount = b.price + this.calculateRequiredAdditionalServicesAmount(b).serviceAmount;

    return aTotalAmountAmount - bTotalAmountAmount;
  }

  calculateRequiredAdditionalServicesAmount(product): { serviceAmount: number; vatAmount: number } {
    if (!product.additional_services) {
      return { serviceAmount: 0, vatAmount: 0 };
    }

    return Object.values(product.additional_services).reduce((acc, service: any) => {
      if (service.required && service.active && !service.disabled) {
        const amount = parseFloat(service.price.amount);
        const vatRate = service.price.vat_rate || 0;
        acc.serviceAmount += amount;
        acc.vatAmount += amount * (vatRate / 100);
      }
      return acc;
    }, { serviceAmount: 0, vatAmount: 0 });
  }

  hideOverLayWhenAllAreLoaded() {
    const allLoaded = this.searchResults.filter(element => element.loaded === true);

    if (allLoaded.length === this.searchResults.length) {
      this.showOverlay = false;
    } else {
      this.showOverlay = true;
    }

    /*
      this.spinnerLoadingPercent = Math.round((allLoaded.length / this.searchResults.length) * 100);
    */
  }

  handleSearch(searchResult) {
    // console.log('handleSearch');
    // console.log(counter + '/' + this.searchResults.length);
    const data = this.consignmentService.prepareSearchData(searchResult.params);
    data.search_id = searchResult.search_id;
    // console.log(data);
    this.shipmentService.search(data)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (response) => {
          // console.log('start: ' + this.completed + '/' + this.searchResults.length);
          if (response.carriers && response.carriers.length) {
            const carriers = response.carriers;
            this.consignmentService
              .saveSearchResultToState(
                carriers,
                data,
                this.deliveryTypes,
                searchResult.id,
                searchResult.search_id || response.search_id
              );
          } else {
            this.store.dispatch({
              type: SEARCH_RESULT_UPDATE,
              payload: {
                id: searchResult.id,
                loaded: true,
                loading: false
              },
            });
          }
        },
        () =>  {
          this.store.dispatch({
            type: SEARCH_RESULT_UPDATE,
            payload: {
              id: searchResult.id,
              loaded: true,
              loading: false
            },
          });
        },
        () => {
          // this.spinnerLoadingPercent = Math.round((counter / this.searchResults.length) * 100);
          this.store.dispatch({type: ACTIVE_SEARCH_REMOVE});
          this.store.dispatch({
            type: SEARCH_RESULT_UPDATE,
            payload: {
              id: searchResult.id,
              loaded: true,
              loading: false
            },
          });
        });
    // console.log(this.completed + '/' + this.searchResults.length);
  }

  handleReloadSearchRequest(searchResult) {
    // console.log('update...');
    this.store.dispatch({
      type: SEARCH_RESULT_UPDATE,
      payload: {
        id: searchResult.id,
        loaded: false,
        loading: true
      },
    });
    this.handleSearch(searchResult);
  }

  get failureExist() {
    return this.searchResults.length > 1 && this.searchResults.find((searchResult: any) => {
      return searchResult.loaded && !searchResult.products.length;
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/search-results/search-results.component.ts